import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';
import {
    MdMailOutline,
    MdPhoneIphone,
} from "react-icons/md";
import { cloneDeep } from 'lodash';
import { Skeleton } from 'antd';

import classes from './ContactSettings.module.scss';
import axios from '../../../../services/axios'
import { setMessage, checkIfContactsAreVerified } from '../../../../store/actions';

import Contact from './Contact/Contact';
import AccountBlocked from '../../../UI/AccountBlocked/AccountBlocked';

const contactsData = [
    {
        typeId: 4,
        name: 'Номер телефона',
        href: 'tel:',
        ifEdit: false,
        message: 'Ссылка для подтверждения отправлена на указанный номер телефона',
        icon: <MdPhoneIphone />
    },
    {
        typeId: 6,
        name: 'E-mail',
        href: 'mailto:',
        ifEdit: false,
        message: 'Ссылка для подтверждения отправлена на указанный электронный адрес',
        icon: <MdMailOutline />,
        validationRules: [
            {
                type: "email",
                message: "Некорректный формат электронной почты!",
            },
            {
                required: true,
                message: 'Введите адрес электронной почты!',
            },
        ],
        regex: /^(?=.{3,30}@)[A-Za-z0-9]+([.\-_]?[A-Za-z0-9])*@[A-Za-z0-9-]+(\.[A-Za-z0-9]+)*(\.[A-Za-z]{2,3})$/i,
    }
]

const ContactSettings = () => {
    const CODE_LIFETIME = 90000
    const BAN_DURATION = 3600000

    const dispatch = useDispatch()
    const routerHistory = useHistory()

    const [loadingContacts, setLoadingContacts] = useState(false)
    const [contactsList, setContactsList] = useState([])
    const [contactNewValue, setContactNewValue] = useState('')
    const [updateContactLoading, setUpdateContactLoading] = useState(false)

    const [isConfirmationCodeSend, setIsConfirmationCodeSend] = useState(null)
    const [isConfirmationCodeValid, setIsConfirmationCodeValid] = useState(true)
    const [confirmationCode, setConfirmationCode] = useState('')
    const [confirmationCodeLoading, setConfirmationCodeLoading] = useState(false)
    
    const [numberOfAttempts, setNumberOfAttempts] = useState(4)
    const [loadingBanExpirationTime, setLoadingBanExpirationTime] = useState(false)
    const [banExpirationTime, setBanExpirationTime] = useState(null)
    const [submitContactLoading, setSubmitContactLoading] = useState(false)

    const getContacts = useCallback(() => {
        if (!banExpirationTime) {
            setLoadingBanExpirationTime(false)
            setLoadingContacts(true)
            axios.get('/api/v1/settings/contacts')
                .then(response => {
                    getContactsSuccess(response)
                })
                .catch((error) => {
                    setContactsList(null)
                    setLoadingContacts(false)
                    if (error?.response?.status === 429) {
                        if (error.response.data.banExpiredTime) setBanExpirationTime(Date.parse(error.response.data.banExpiredTime))
                        else dispatch(setMessage({ type: 'error', text: 'Код подтверждения еще не истек.' }))
                    } else dispatch(setMessage({ type: 'error', text: 'Не удалось получить контактные данные.' }))
                })
        }
    }, [banExpirationTime, dispatch])

    const getContactsSuccess = (response) => {
        let tmp = cloneDeep(response.data.contactDtoList)
        tmp = tmp.map(contact => ({
            ...contact,
            ...contactsData.find(method => method.typeId === contact.contactTypeId),
            ifEdit: !contact.contactValue
        }))
        setContactsList(tmp)
        setLoadingContacts(false)

        setNumberOfAttempts(response.data.numberOfAttempts)

        const contactWithAliveCode = tmp.find(contact => contact.codeExpirationTime !== null)
        if (contactWithAliveCode) {
            setIsConfirmationCodeSend({ contactId: contactWithAliveCode.id,  expirationTime: Date.parse(contactWithAliveCode.codeExpirationTime), expired: false })
        }
    }

    useEffect(() => {
        getContacts()
    }, [getContacts])

    useEffect(() => {
        if (numberOfAttempts === 0) setBanExpirationTime(new Date(new Date().getTime() + BAN_DURATION))
    }, [numberOfAttempts])

    const setLoadingSkeleton = () => {
        return <div className={classes.contactsContainer}>
            <div className={classes.skeletonContainer}><Skeleton active /><Skeleton active /></div>
            <div className={classes.skeletonContainer}><Skeleton active /><Skeleton active /></div>
        </div>
    }

    const areEditButtonsVisible = () => {
        return contactsList.every(contact => contact.contactValue !== null)
    }

    const contactSetEdit = (index) => {
        setContactNewValue('')
        const tmp = cloneDeep(contactsList)
        tmp.forEach(item => item.ifEdit = false)
        tmp[index].ifEdit = true
        setContactsList(tmp)
    }

    const cancelEdit = () => {
        setContactNewValue('')
        const tmp = cloneDeep(contactsList)
        tmp.forEach(item => item.ifEdit = false)
        setContactsList(tmp)
    }

    const updateCreateContact = (index) => {
        setUpdateContactLoading(true)
        const tmp = cloneDeep(contactsList)
        let path = ''
        let dto = null
        if (tmp[index].contactValue) {
            path = '/api/v1/settings/contact/update'
            dto = {
                id: tmp[index].id,
                contactValue: contactNewValue
            }
        } else {
            path = '/api/v1/settings/contact/create'
            dto = {
                contactValue: contactNewValue,
                contactTypeId: tmp[index].contactTypeId,
            }
        }
        axios.post(path, dto)
            .then((response) => {
                setUpdateContactLoading(false)
                tmp[index].ifEdit = false
                tmp[index].confirmed = false
                tmp[index].contactValue = contactNewValue
                if (response.data) tmp[index].id = response.data
                setContactsList(tmp)
                setContactNewValue('')
                dispatch(setMessage({ type: 'success', text: "Контакт успешно изменен." }))
            })
            .catch((error) => {
                setUpdateContactLoading(false)
                if (error?.response?.status === 409) dispatch(setMessage({ type: 'warning', text: "Данные не должны совпадать." }))
                else dispatch(setMessage({ type: 'error', text: "Не удалось изменить контактные данные." }))
            })
    }

    const validateContactValue = (index) => {
        const target = contactsList[index]
        const value = target.ifEdit ? contactNewValue : target.contactValue
        if (target.contactTypeId === 4) {
            return value.match(/^\+375(25|29|33|44)\d{7}$/)
        }
        return target.regex.test(value)
    }

    const sendConfirmationCode = (index) => {
        setConfirmationCodeLoading(true)
        const target = contactsList[index]
        setIsConfirmationCodeValid(true)
        setIsConfirmationCodeSend(oldValue => ({ ...oldValue, expirationTime: null, expired: false }))

        axios.post('/api/v1/settings/contact/send-confirm-message', target.id, {
            headers: { "Content-type": "application/json" },
        })
            .then(() => {
                setConfirmationCodeLoading(false)
                setIsConfirmationCodeSend({ contactId: target.id, expirationTime: new Date(new Date().getTime() + CODE_LIFETIME).getTime(), expired: false })
                setNumberOfAttempts(oldValue => oldValue - 1)
                dispatch(setMessage({ type: 'success', text: `Код отправлен на указанный ${target.name.toLowerCase()}.` }))
            })
            .catch((error) => {
                setConfirmationCodeLoading(false)
                if (error?.response?.status === 429) {
                    if (error.response.data.banExpiredTime) setBanExpirationTime(Date.parse(error.response.data.banExpiredTime))
                    else dispatch(setMessage({ type: 'warning', text: `Код уже был отправлен на указанный ${target.name.toLowerCase()}.` })) //
                } else dispatch(setMessage({ type: 'warning', text: `Не удалось отправить код на указанный ${target.name.toLowerCase()}.` })) //                
            })
    }

    const onCodeExpireHandler = () => {
        setTimeout(() => {
            setIsConfirmationCodeSend(oldValue => ({ ...oldValue, expirationTime: null, expired: true }))
            if (numberOfAttempts == 0) setBanExpirationTime(new Date(new Date().getTime() + BAN_DURATION))
        }, 1000);
    }

    const onBanExpireHandler = () => {
        setLoadingBanExpirationTime(true)
        setTimeout(() => {
            setBanExpirationTime(null)
        }, 2000);
    }

    const submitContact = (index) => {
        setSubmitContactLoading(true)
        if (!isConfirmationCodeSend.expired) {
            axios.post('api/v1/settings/contact/confirm', {
                contactId: isConfirmationCodeSend.contactId,
                code: confirmationCode
            })
                .then(() => {
                    setSubmitContactLoading(false)
                    setConfirmationCode('')
                    setIsConfirmationCodeSend(null)
                    const tmp = cloneDeep(contactsList)
                    tmp[index].confirmed = true
                    setContactsList(tmp)
                    dispatch(setMessage({ type: 'success', text: `${contactsList[index].name} успешно подтвержден.` }))
                    checkAllContactsConfirmed(tmp)
                })
                .catch((error) => {
                    setSubmitContactLoading(false)
                    setConfirmationCode('')
                    setNumberOfAttempts(oldValue => oldValue - 1)
                    switch (error?.response?.status) {
                        case 404:
                            dispatch(setMessage({ type: 'error', text: 'Указанные контактные данные не найдены.' }))
                            break;
                        case 409:
                            setIsConfirmationCodeValid(false)
                            dispatch(setMessage({ type: 'error', text: 'Неверный код. Пожалуйста, введите код, который Вы только что получили.' }))
                            break;
                        case 410:
                            setIsConfirmationCodeValid(false)
                            setIsConfirmationCodeSend(oldValue => ({ ...oldValue, expired: true }))
                            dispatch(setMessage({ type: 'error', text: 'Код подтверждения истек.' }))
                            break;
                        case 429:
                            setBanExpirationTime(new Date(new Date().getTime() + BAN_DURATION))
                            break;
                        default:
                            dispatch(setMessage({ type: 'error', text: 'Не удалось подтвердить контакт.' }))
                    }
                })
        }
    }

    const checkAllContactsConfirmed = (contacts) => {
        if (contacts.every(contact => contact.confirmed === true)) {
            dispatch(checkIfContactsAreVerified(false))
            routerHistory.replace('/personal-account/profile')
        }
    }

    return (<div className='ContactSettings_container_color'>
        {(((loadingContacts || !contactsList) && !banExpirationTime) || (!loadingContacts && loadingBanExpirationTime && !contactsList)) && setLoadingSkeleton()}
        {(!loadingContacts && contactsList && !banExpirationTime && !loadingBanExpirationTime) && <div className={classes.contactsContainer}>
            {
                contactsList.map((contact, index) => <Contact
                    key={index}
                    contact={contact}
                    contactSetEdit={() => contactSetEdit(index)}
                    cancelEdit={() => cancelEdit()}
                    contactNewValue={contactNewValue}
                    contactNewValueOnChangeHandler={(e) => setContactNewValue(e.target.value)}
                    updateCreateContact={() => updateCreateContact(index)}
                    updateContactLoading={updateContactLoading}
                    areEditButtonsVisible={areEditButtonsVisible()}
                    phoneInputHandler={setContactNewValue}
                    validateContactValue={() => validateContactValue(index)}
                    isConfirmationCodeSend={isConfirmationCodeSend}
                    sendConfirmationCode={() => sendConfirmationCode(index)}
                    isConfirmationCodeValid={isConfirmationCodeValid}
                    confirmationCode={confirmationCode}
                    confirmationCodeLoading={confirmationCodeLoading}
                    setConfirmationCode={(value) => setConfirmationCode(value)}
                    onCodeExpireHandler={onCodeExpireHandler}
                    numberOfAttempts={numberOfAttempts}
                    submitContactLoading={submitContactLoading}
                    submitContact={() => submitContact(index)} />)
            }
        </div>}
        {(!loadingContacts && banExpirationTime && !loadingBanExpirationTime) && <div className={classes.banContainer}>
            {banExpirationTime && <AccountBlocked banExpirationTime={banExpirationTime} onExpireHandler={onBanExpireHandler} />}
        </div>}
    </div>
    )
}

export default ContactSettings